import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const PLAYBACK_SPEEDS = [1, 2, 4, 8, 16];

/**
 * Horloge unique d'une activité : carte, vidéo, messages et timeline lisent
 * tous le même instant `t` (epoch ms), renvoyé à part de `clock` (stable). Quand une vidéo joue, c'est elle qui
 * donne l'heure (via ses tags ID3) ; sinon l'horloge avance seule.
 */
export function useActivityClock(startMs, endMs) {
    const [t, setT] = useState(startMs ?? 0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const tRef = useRef(t);
    const driverRef = useRef(null);
    const seekListenersRef = useRef(new Set());

    useEffect(() => {
        tRef.current = startMs ?? 0;
        setT(startMs ?? 0);
        setPlaying(false);
    }, [startMs, endMs]);

    const clamp = useCallback(value => {
        if (!Number.isFinite(value)) return startMs ?? 0;
        return Math.min(endMs ?? value, Math.max(startMs ?? value, value));
    }, [startMs, endMs]);

    useEffect(() => {
        if (!playing) return undefined;
        let frame = null;
        let last = performance.now();
        const loop = now => {
            const delta = now - last;
            last = now;
            const driven = driverRef.current ? driverRef.current() : null;
            const next = clamp(Number.isFinite(driven) ? driven : tRef.current + delta * speed);
            tRef.current = next;
            setT(next);
            if (endMs !== null && endMs !== undefined && next >= endMs) {
                setPlaying(false);
                return;
            }
            frame = requestAnimationFrame(loop);
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
    }, [playing, speed, clamp, endMs]);

    const seek = useCallback(value => {
        const next = clamp(value);
        tRef.current = next;
        setT(next);
        seekListenersRef.current.forEach(listener => listener(next));
    }, [clamp]);

    const play = useCallback(() => {
        if (endMs !== null && endMs !== undefined && tRef.current >= endMs) seek(startMs);
        setPlaying(true);
    }, [endMs, startMs, seek]);

    const pause = useCallback(() => setPlaying(false), []);

    /** Une source média devient l'horloge (ou la rend avec null). */
    const setDriver = useCallback(driver => { driverRef.current = driver; }, []);
    /** Rend l'horloge seulement si on la tient encore (plusieurs lecteurs partagent la même horloge). */
    const releaseDriver = useCallback(driver => { if (driverRef.current === driver) driverRef.current = null; }, []);
    const hasDriver = useCallback(() => driverRef.current !== null, []);

    /** Prévenu à chaque saut manuel (glisser la timeline, clic sur un message). */
    const onSeek = useCallback(listener => {
        seekListenersRef.current.add(listener);
        return () => seekListenersRef.current.delete(listener);
    }, []);

    // Objet stable entre deux images : seul `t` change à chaque frame, les
    // effets abonnés à `clock` ne se relancent qu'au changement de lecture ou de vitesse.
    const clock = useMemo(() => ({
        tRef, playing, speed, setSpeed, play, pause, seek, setDriver, releaseDriver, hasDriver, onSeek, startMs, endMs,
    }), [playing, speed, play, pause, seek, setDriver, releaseDriver, hasDriver, onSeek, startMs, endMs]);

    return { t, clock };
}
